/**
 * On-chain service — Qr20Channel.sol contract interactions via ethers v6.
 *
 * Features: approve, openChannel, closeChannel, reclaimFunds, gas estimation,
 * nonce management, retry on revert.
 */

import { ethers } from 'ethers';
import { QR20_CHANNEL_ABI, ERC20_ABI, getChainConfig, retry } from '@tokenstreaming/shared';
import type { DistributionRecipient } from './types.js';

export class OnChainService {
  private provider: ethers.JsonRpcProvider;
  private chainId: string;
  private contractAddress: string;

  constructor(chainId: string) {
    this.chainId = chainId;
    const config = getChainConfig(chainId);
    this.contractAddress = config.contracts.qr20Channel;
    this.provider = new ethers.JsonRpcProvider(config.rpcUrls[0]);
  }

  /**
   * Check ERC-20 allowance and approve if needed.
   */
  async ensureAllowance(
    tokenAddress: string,
    amount: bigint,
    wallet: ethers.Wallet
  ): Promise<string | null> {
    const signer = wallet.connect(this.provider);
    const token = new ethers.Contract(tokenAddress, ERC20_ABI, signer);

    const currentAllowance = await token.allowance!(wallet.address, this.contractAddress) as bigint;
    if (currentAllowance >= amount) {
      return null; // Already approved
    }

    const tx = await token.approve!(this.contractAddress, amount) as ethers.TransactionResponse;
    const receipt = await tx.wait(1);
    return receipt?.hash ?? tx.hash;
  }

  /**
   * Open a channel on-chain with ERC-20 deposit.
   */
  async openChannel(
    channelId: string,
    resolverAddress: string,
    tokenAddress: string,
    deposit: bigint,
    disputeWindowS: number,
    wallet: ethers.Wallet
  ): Promise<{ txHash: string }> {
    const signer = wallet.connect(this.provider);
    const contract = new ethers.Contract(this.contractAddress, QR20_CHANNEL_ABI, signer);

    const tx = await retry(async () => {
      return contract.openChannel!(
        channelId,
        resolverAddress,
        tokenAddress,
        deposit,
        disputeWindowS
      ) as Promise<ethers.TransactionResponse>;
    }, 2, 2000);

    const receipt = await tx.wait(1);
    if (!receipt || receipt.status !== 1) {
      throw new Error('openChannel transaction reverted');
    }

    return { txHash: receipt.hash };
  }

  /**
   * Close a channel on-chain with bilateral signatures + Distribution.
   */
  async closeChannel(
    channelId: string,
    sequence: number,
    creatorAmount: bigint,
    resolverAmount: bigint,
    jcsStateBytes: Uint8Array,
    creatorSig: Uint8Array,
    resolverSig: Uint8Array,
    distributionRecipients: DistributionRecipient[],
    distributionSourceAmount: bigint,
    wallet: ethers.Wallet
  ): Promise<{ txHash: string }> {
    const signer = wallet.connect(this.provider);
    const contract = new ethers.Contract(this.contractAddress, QR20_CHANNEL_ABI, signer);

    // Convert distribution recipients to contract format
    const contractRecipients = distributionRecipients
      .filter(r => r.role !== 'receiver') // Only fee recipients go to contract
      .map(r => ({
        recipient: r.recipient.split('/').pop() ?? r.recipient, // Extract address from CAIP-10
        bps: r.bps,
      }));

    const tx = await retry(async () => {
      return contract.closeChannel!(
        channelId,
        sequence,
        creatorAmount,
        resolverAmount,
        jcsStateBytes,
        creatorSig,
        resolverSig,
        contractRecipients,
        distributionSourceAmount
      ) as Promise<ethers.TransactionResponse>;
    }, 2, 2000);

    const receipt = await tx.wait(1);
    if (!receipt || receipt.status !== 1) {
      throw new Error('closeChannel transaction reverted');
    }

    return { txHash: receipt.hash };
  }

  /**
   * Reclaim funds after dispute window expiry.
   */
  async reclaimFunds(
    channelId: string,
    wallet: ethers.Wallet
  ): Promise<{ txHash: string }> {
    const signer = wallet.connect(this.provider);
    const contract = new ethers.Contract(this.contractAddress, QR20_CHANNEL_ABI, signer);

    const tx = await contract.reclaimFunds!(channelId) as ethers.TransactionResponse;
    const receipt = await tx.wait(1);
    if (!receipt || receipt.status !== 1) {
      throw new Error('reclaimFunds transaction reverted');
    }

    return { txHash: receipt.hash };
  }

  /**
   * Check if a channel is open on-chain.
   */
  async isChannelOpen(channelId: string): Promise<boolean> {
    const contract = new ethers.Contract(this.contractAddress, QR20_CHANNEL_ABI, this.provider);
    return contract.isChannelOpen!(channelId) as Promise<boolean>;
  }

  /**
   * Read channel state from contract.
   */
  async getChannelData(channelId: string): Promise<{
    creator: string;
    resolver: string;
    token: string;
    totalDeposited: bigint;
    disputeWindowSeconds: bigint;
    openedAt: bigint;
    sequence: bigint;
    closed: boolean;
  } | null> {
    const contract = new ethers.Contract(this.contractAddress, QR20_CHANNEL_ABI, this.provider);
    const hash = await contract.getChannelIdHash!(channelId) as string;
    const data = await contract.channels!(hash);
    if (data.creator === ethers.ZeroAddress) return null;
    return {
      creator: data.creator as string,
      resolver: data.resolver as string,
      token: data.token as string,
      totalDeposited: data.totalDeposited as bigint,
      disputeWindowSeconds: data.disputeWindowSeconds as bigint,
      openedAt: data.openedAt as bigint,
      sequence: data.sequence as bigint,
      closed: data.closed as boolean,
    };
  }

  getContractAddress(): string {
    return this.contractAddress;
  }
}
